import { ModelDataType } from 'src/app/interfaces/model-data-type';

import { Address } from './address';
import { ApConfig } from './ap-config';
import { BaseModel } from './base-model';
import { ClientRegisteredVendor } from './client-registered-vendor';
import { ExemptVendor } from './exempt-vendor';
import { Vendor } from './vendor';

export class ApVendor extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static ApVendorProperties: (
    | {
        property: keyof ApVendor;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof ApVendor;
        type: 'alias';
      }
    | {
        property: keyof ApVendor;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'ApConfigId', type: 'column', dataType: 'number' },
    { property: 'vendorKey', type: 'column', dataType: 'string' },
    { property: 'migrationKey', type: 'column', dataType: 'string' },
    { property: 'resourceId', type: 'column', dataType: 'number' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    {
      property: 'ClientRegisteredVendorId',
      type: 'column',
      dataType: 'number'
    },
    { property: 'ExemptVendorId', type: 'column', dataType: 'number' },
    { property: 'InvitationContactId', type: 'column', dataType: 'number' },
    { property: 'serviceTypeId', type: 'column', dataType: 'number' },
    { property: 'legalName', type: 'column', dataType: 'string' },
    { property: 'dba', type: 'column', dataType: 'string' },
    { property: 'ein', type: 'column', dataType: 'string' },
    { property: 'ssn', type: 'column', dataType: 'string' },
    { property: 'companyType', type: 'column', dataType: 'string' },
    { property: 'phone', type: 'column', dataType: 'string' },
    { property: 'phone2', type: 'column', dataType: 'string' },
    { property: 'fax', type: 'column', dataType: 'string' },
    { property: 'email', type: 'column', dataType: 'string' },
    { property: 'primaryContactName', type: 'column', dataType: 'string' },
    { property: 'isExempt', type: 'column', dataType: 'boolean' },
    { property: 'status', type: 'column', dataType: 'string' },
    { property: 'wasStatusSet', type: 'column', dataType: 'boolean' },
    { property: 'wasHoldSet', type: 'column', dataType: 'boolean' },
    { property: 'statusDate', type: 'column', dataType: 'date' },
    { property: 'onHold', type: 'column', dataType: 'boolean' },
    { property: 'onHoldDate', type: 'column', dataType: 'date' },
    { property: 'isMissing', type: 'column', dataType: 'boolean' },
    { property: 'isForced', type: 'column', dataType: 'boolean' },
    { property: 'syncAt', type: 'column', dataType: 'date' },
    { property: 'nextSync', type: 'column', dataType: 'date' },
    { property: 'lastSync', type: 'column', dataType: 'date' },
    { property: 'syncStatus', type: 'column', dataType: 'string' },
    { property: 'vendorType', type: 'column', dataType: 'string' },
    { property: 'lastTimeOnHoldChecked', type: 'column', dataType: 'date' },
    {
      property: 'nextTimeActiveStatusChecked',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },

    { property: 'linked', type: 'alias' },
    { property: 'viveLegalName', type: 'alias' },
    { property: 'fedId', type: 'alias' },
    { property: 'viveDba', type: 'alias' },
    { property: 'viveEin', type: 'alias' },
    { property: 'viveStatus', type: 'alias' },
    { property: 'viveVendorId', type: 'alias' },
    { property: 'statusMismatch', type: 'alias' },
    { property: 'confidence', type: 'alias' },
    { property: 'isLinkedToSpecificVendor', type: 'alias' },
    { property: 'hasMetaData', type: 'alias' },

    { property: 'ApConfig', model: 'ApConfig', type: 'association' },
    { property: 'Vendor', model: 'Vendor', type: 'association' },
    { property: 'ExemptVendor', model: 'ExemptVendor', type: 'association' },
    {
      property: 'ClientRegisteredVendor',
      model: 'ClientRegisteredVendor',
      type: 'association'
    },
    { property: 'Addresses', model: 'Address', type: 'association' },
    { property: 'StreetAddress', model: 'StreetAddress', type: 'association' },
    { property: 'MailingAddress', model: 'MailingAddress', type: 'association' }
  ];

  static {
    ApVendor.ApVendorProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        ApVendor.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        ApVendor.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        ApVendor.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(ApVendorObject: Partial<ApVendor>) {
    super();

    ApVendor.ApVendorProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = ApVendorObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  ApConfigId?: number;
  vendorKey?: string;
  migrationKey?: string;
  resourceId?: number;
  VendorId?: number | null;
  ClientRegisteredVendorId?: number | null;
  ExemptVendorId?: number | null;
  InvitationContactId?: number;
  serviceTypeId?: number;
  legalName?: string;
  dba?: string;
  ein?: string;
  ssn?: string;
  companyType?: string;
  phone?: string;
  phone2?: string;
  fax?: string;
  email?: string;
  primaryContactName?: string;
  isExempt?: boolean;
  status?: 'Active' | 'Inactive';
  wasStatusSet?: boolean;
  wasHoldSet?: boolean;
  statusDate?: Date;
  onHold?: boolean;
  onHoldDate?: Date;
  isMissing?: boolean;
  isForced?: boolean;
  syncAt?: Date | null;
  nextSync?: Date | null;
  lastSync?: Date | null;
  syncStatus?: string;
  vendorType?: string;
  lastTimeOnHoldChecked?: Date | null;
  nextTimeActiveStatusChecked?: Date | null;
  createdAt?: Date;
  updatedAt?: Date;
  // address1?: string;
  // address2?: string;
  // city?: string;
  // state?: string;
  // zip?: string;
  // mailingAddress1?: string;
  // mailingAddress2?: string;
  // mailingCity?: string;
  // mailingState?: string;
  // mailingZip?: string;

  ApConfig?: ApConfig;
  Vendor?: Vendor;
  ExemptVendor?: ExemptVendor;
  ClientRegisteredVendor?: ClientRegisteredVendor;
  Addresses?: Address[];
  StreetAddress?: Address;
  MailingAddress?: Address;

  // Aliased columns.
  linked?: boolean;
  viveLegalName?: string;
  fedId?: string;
  viveDba?: string;
  viveEin?: string;
  viveStatus?: string;
  viveVendorId?: number;
  statusMismatch?: boolean;
  confidence?: number;
  isLinkedToSpecificVendor?: boolean;
  hasMetaData?: boolean;
}

/*
ApVendor.associate = (models) => {
  ApVendor.belongsTo(models.InvitationContact, { foreignKey: 'InvitationContactId' });
  ApVendor.hasMany(models.ApVendorLog, { foreignKey: 'ApVendorId' });
};
*/
